import type { Session } from '@orgus/protocol';
import { useEffect } from 'react';
import { useOrgus } from '../../lib/store.ts';
import { toast } from '@orgus/ui';
import { useSessionParam } from '../sessions/useSessionParam.ts';
import { NewSessionDialog } from './NewSessionDialog.tsx';
import { registerHost, setPending, useNewSession } from './store.ts';
import type { PendingStart } from './store.ts';

/** How long to wait for a started session to show up before saying so. */
export const APPEAR_TIMEOUT_MS = 60_000;

/** The session a start produced: by id when the machine returned one, else the newest match. */
export function findStarted(sessions: Record<string, Session>, p: PendingStart): Session | undefined {
  if (p.sessionId) return sessions[p.sessionId];
  return Object.values(sessions)
    .filter(
      (s) =>
        s.machineId === p.machineId &&
        s.origin === 'orgus' &&
        s.startedAt >= p.since - 5_000 &&
        (p.checkoutId ? s.checkoutId === p.checkoutId : s.projectId === p.projectId),
    )
    .sort((a, b) => b.startedAt - a.startedAt)[0];
}

/** Mount on a page to make the New session dialog available there, and open the started session's drawer. */
export function NewSessionHost() {
  const [, setSession] = useSessionParam();
  const pending = useNewSession((s) => s.pending);

  useEffect(() => registerHost(), []);

  useEffect(() => {
    if (!pending) return;
    const check = () => {
      const s = findStarted(useOrgus.getState().sessions, pending);
      if (s) {
        setPending(null);
        setSession(s.id, { replace: false });
        return true;
      }
      return false;
    };
    if (check()) return;
    const unsub = useOrgus.subscribe((st, prev) => {
      if (st.sessions !== prev.sessions) check();
    });
    const left = APPEAR_TIMEOUT_MS - (Date.now() - pending.since);
    const timer = setTimeout(() => {
      if (useNewSession.getState().pending !== pending) return;
      setPending(null);
      toast('The new session has not appeared yet', {
        description: 'Claude may still be starting; check the machine if it does not show up soon.',
      });
    }, Math.max(0, left));
    return () => {
      unsub();
      clearTimeout(timer);
    };
  }, [pending, setSession]);

  return <NewSessionDialog />;
}
